/**
 * Screen 3 / Wave 2.8 — empty-q Seznam Tenanti (`listKind=tenant`).
 * Public brand directory via `/public/customer-tenants` (TenantSelect /orgs parity).
 */
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  toSameOriginCatalogImageUrl,
  classifyCatalogImageUrl,
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
} from 'pi-kiosk-shared';
import { useTheme } from '../../app/ThemeProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { invalidatePublicCustomerTenantsCache } from '../../shared/api/customerDirectoryCacheInvalidate.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { DirectoryEntityRow } from '../../shared/ui/DirectoryEntityRow.js';
import {
  DirectoryResults,
  DirectoryResultsSkeleton,
} from '../../shared/ui/DirectoryResults.js';
import {
  DIRECTORY_EMPTY_PANEL_MAX_CLASS,
  DIRECTORY_NEARBY_INSET_ROW_CLASS,
} from '../../shared/ui/directoryPickerLayout.js';
import { useOrgDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import {
  directoryThumbFetchPriority,
  directoryThumbLoading,
} from '../../shared/ui/directoryThumbLoading.js';
import { filterDirectoryOrgs } from '../../shared/ui/directoryListFilter.js';
import {
  orgDirectoryQueryOptions,
} from '../tenant-select/hooks/useOrgDirectoryRealtimeSync.js';
import {
  fetchPublicCustomerTenants,
  type PublicCustomerTenantDTO,
} from '../tenant-select/publicTenantApi.js';
import { resolveDirectoryShopOpenAction } from '../shops/resolveDirectoryShopOpenAction.js';
import { usePublicDirectorySseConnected } from '../shops/hooks/PublicDirectorySseProvider.js';

export type BrowseTenantDirectoryResultsProps = {
  /** Client-filter query (D22 — full tenant list + contains match). */
  readonly query?: string;
  /** When false, omit the Tenanti (N) H2 (Vše section supplies its own header). */
  readonly showHeading?: boolean;
  /** When set, render these rows instead of fetching (Vše compose). */
  readonly items?: readonly PublicCustomerTenantDTO[];
  readonly isPending?: boolean;
  readonly isError?: boolean;
  readonly onRetry?: () => void;
};

export function BrowseTenantDirectoryResults(
  props: BrowseTenantDirectoryResultsProps = {},
): JSX.Element {
  const {
    query = '',
    showHeading = true,
    items: itemsProp,
    isPending: isPendingProp,
    isError: isErrorProp,
    onRetry,
  } = props;
  const { t } = useTranslation('customer');
  const { apiBaseUrl } = useRuntimeConfig();
  const directorySseConnected = usePublicDirectorySseConnected();
  const useExternal = itemsProp !== undefined;

  const tenantsQuery = useQuery({
    queryKey: customerQueryKeys.publicCustomerTenants(apiBaseUrl),
    queryFn: ({ signal }) => fetchPublicCustomerTenants(apiBaseUrl, signal),
    ...orgDirectoryQueryOptions(directorySseConnected),
    enabled: !useExternal,
  });

  const visibleTenants = useMemo(
    () =>
      filterDirectoryOrgs(itemsProp ?? tenantsQuery.data ?? [], {
        query,
        sort: 'nameAsc',
      }),
    [itemsProp, query, tenantsQuery.data],
  );

  const isPending = useExternal ? (isPendingProp ?? false) : tenantsQuery.isPending;
  const isError = useExternal ? (isErrorProp ?? false) : tenantsQuery.isError;

  if (isPending) {
    return (
      <div data-testid="customer-browse-tenant-directory" data-state="loading">
        <DirectoryResultsSkeleton
          density="list"
          listLayout="inset"
          size="md"
          rowDensity="compact"
          count={4}
          testId="customer-browse-tenant-directory-skeleton"
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="customer-browse-tenant-directory" data-state="error">
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('browse.discovery.tenantDirectoryError', {
              defaultValue: t('tenantSelect.loadError'),
            })}
          </p>
          <Button
            type="button"
            intent="secondary"
            block
            className="mt-4"
            data-testid="customer-browse-tenant-directory-retry"
            onClick={() => {
              if (onRetry !== undefined) {
                onRetry();
                return;
              }
              void tenantsQuery.refetch();
            }}
          >
            {t('browse.discovery.tenantDirectoryRetry', {
              defaultValue: t('tenantSelect.retry'),
            })}
          </Button>
        </Card>
      </div>
    );
  }

  if (visibleTenants.length === 0) {
    return (
      <div data-testid="customer-browse-tenant-directory" data-state="empty">
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('browse.discovery.tenantDirectoryEmpty', {
              defaultValue: t('tenantSelect.empty'),
            })}
          </p>
        </Card>
      </div>
    );
  }

  const tenantCount = visibleTenants.length;

  return (
    <div
      className="space-y-3"
      data-testid="customer-browse-tenant-directory"
      data-state="ready"
      data-tenant-count={String(tenantCount)}
    >
      {showHeading ? (
        <h2
          id="customer-browse-tenant-directory-heading"
          className="text-base font-semibold text-[var(--color-on-surface)]"
          data-testid="customer-browse-tenant-directory-heading"
        >
          {t('browse.discovery.tenantDirectoryHeading', {
            count: tenantCount,
            defaultValue: `Business partners (${tenantCount})`,
          })}
        </h2>
      ) : null}
      <DirectoryResults
        density="list"
        listLayout="inset"
        testId="customer-browse-tenant-directory-results"
        aria-live="polite"
      >
        {visibleTenants.map((tenant, index) => (
          <BrowseTenantDirectoryRow
            key={tenant.code}
            tenant={tenant}
            index={index}
            apiBaseUrl={apiBaseUrl}
          />
        ))}
      </DirectoryResults>
    </div>
  );
}

function BrowseTenantDirectoryRow({
  tenant,
  index,
  apiBaseUrl,
}: {
  readonly tenant: PublicCustomerTenantDTO;
  readonly index: number;
  readonly apiBaseUrl: string;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const { effectiveTheme } = useTheme();
  const logoUrl = toSameOriginCatalogImageUrl(tenant.logoUrl, {
    tenantCode: tenant.code,
  });
  const logoUrlDark = toSameOriginCatalogImageUrl(tenant.logoUrlDark, {
    tenantCode: tenant.code,
  });

  const onMediaFailed = useCallback(
    (failedSrc: string) => {
      if (classifyCatalogImageUrl(failedSrc) === 'signed_api') {
        invalidatePublicCustomerTenantsCache(queryClient, apiBaseUrl);
      }
    },
    [apiBaseUrl, queryClient],
  );

  const { thumbProps } = useOrgDirectoryThumb({
    logoUrl,
    logoUrlDark,
    monogram: {
      id: tenant.tenantId,
      displayName: tenant.name,
      code: tenant.code,
    },
    // G2 / D23 — search Tenanti compact identity (52–60 md), not orgPicker lg.
    size: 'md',
    logoChipRim: normalizeLogoChipRimSettings(tenant),
    logoChipBackground: normalizeLogoChipBackgroundSettings(tenant),
    logoChipRimTheme: effectiveTheme,
    loading: directoryThumbLoading(index),
    fetchPriority: directoryThumbFetchPriority(index),
    onMediaFailed,
    testId: `customer-browse-tenant-thumb-${tenant.code}`,
  });

  const shopsUrl = `/${encodeURIComponent(tenant.code)}/shops`;
  const rowPressHref =
    resolveDirectoryShopOpenAction({
      customerShopUrl: shopsUrl,
      targetTenantCode: tenant.code,
    })?.url ?? null;

  if (rowPressHref === null) {
    return null;
  }

  return (
    <li
      className={DIRECTORY_NEARBY_INSET_ROW_CLASS}
      data-testid={`customer-browse-tenant-item-${tenant.code}`}
    >
      <DirectoryEntityRow
        thumb={thumbProps}
        title={tenant.name}
        subtitle={tenant.code}
        rowPressHref={rowPressHref}
        rowPressTestId={`customer-browse-tenant-open-${tenant.code}`}
        showChevron
        density="compact"
        testId={`customer-browse-tenant-row-${tenant.code}`}
      />
    </li>
  );
}
